import { createSelector } from '@reduxjs/toolkit';

import { selectCBFriendly, selectChartTab } from '../ui/ui.selector';
import { selectCurrentLocation, selectUseStationData } from '../map/map.selector';
import {
  selectIsClimate,
  selectAttributeInfo,
  selectHazardMetric,
  selectDateRange,
} from '../options/options.selector';

import { getClimateColors, getHazardColors } from './data.utils';

const selectData = (state) => state.data;

export const selectIsLoading = createSelector(
  selectData,
  (data) => data.loading
);

export const selectMapData = createSelector(selectData, (data) => data.map);

export const selectTrend = createSelector(
  selectData,
  selectCurrentLocation,
  (data, loc) => {
    try {
      return data.map[loc.id.length === 2 ? 'state' : 'county'][loc.id];
    } catch {
      return 0;
    }
  }
);

export const selectHazardTextData = createSelector(
  selectData,
  selectCurrentLocation,
  (data, loc) => {
    const isState = loc.id.length === 2;
    const id = isState ? loc.id : parseInt(loc.id);

    const areaData = data.map[isState ? 'state' : 'county'];
    const resData = {};
    ['count', 'damageCrops', 'damageProperty', 'deaths', 'injuries'].forEach(
      (metric) => {
        try {
          resData[metric] = areaData[metric][id] || 0;
        } catch {
          resData[metric] = null;
        }
      }
    );
    return resData;
  }
);

export const selectMapColors = createSelector(
  selectMapData,
  selectCBFriendly,
  selectIsClimate,
  selectAttributeInfo,
  selectHazardMetric,
  (data, cbf, isClimate, attributeInfo, metric) => {
    if (isClimate) {
      return getClimateColors(
        cbf,
        data,
        attributeInfo.precip,
        attributeInfo.isReversed
      );
    } else {
      return getHazardColors(
        data.state[metric],
        data.county[metric],
        metric.includes('damage')
      );
    }
  }
);

export const selectSearchLocation = createSelector(
  selectData,
  (data) => data.searchLocation
);

export const selectSearchIsLoading = createSelector(
  selectData,
  (data) => data.searching
);

export const selectHistoricalChartData = createSelector(
  selectData,
  selectCurrentLocation,
  selectDateRange,
  selectUseStationData,
  (data, loc, dateRange, useStation) => {
    try {
      let slicedData;
      if (useStation) {
        const histStartIdx = data.station.past.findIndex((arr) => arr[0] === dateRange.start);
        const histEndIdx = data.station.past.findIndex((arr) => arr[0] === dateRange.end);
        slicedData = data.station.past.slice(histStartIdx, histEndIdx + 1);
      } else {
        const stateOrCounty = loc.id.length === 2 ? 'state' : 'county';
        const years = data.raw[stateOrCounty].years;
        const histStartIdx = years.findIndex((year) => year === dateRange.start);
        const histEndIdx = years.findIndex((year) => year === dateRange.end);
        const histData = data.raw[stateOrCounty].data[loc.id];
        
        slicedData = [];
        for (let i = histStartIdx; i <= histEndIdx; i++) {
          slicedData.push([years[i], histData[i]]);
        }
      }
      
      return slicedData;
    } catch (e) {
      return [];
    }
  }
);

export const selectProjectedChartData = createSelector(
  selectData,
  selectChartTab,
  selectUseStationData,
  (data, tab, useStation) => useStation ? (data.station.projected[tab] || []) : (data.projected[tab] || [])
);

export const selectClimateCsvData = createSelector(
  selectData,
  selectCurrentLocation,
  (data, loc) => {
    try {
      console.log(data);

      const level = loc.id.length === 2 ? 'state' : 'county';
      
      // Construct historical data
      const historical = [];
      const rawData = data.raw[level].data[loc.id];
      const rawYears = [...data.raw[level].years];

      if (rawYears[rawYears.length - 1] === new Date().getFullYear()) {
        rawYears.pop();
      }

      for (let i = 0; i < rawYears.length; i++) {
        historical.push([rawYears[i], Math.round(rawData[i] * 100) / 100]);
      }

      // Construct projected data
      const projected = [];
      const numProjected = data.projected.high.mean.length;
      for (let i = 0; i < numProjected; i++) {
        const yearRes = ['intermediate','high', 'veryHigh'].map(ssp => {
          const sspData = data.projected[ssp];
          return [sspData.range[i][1], sspData.mean[i][1], sspData.range[i][2]].map(v => Math.round(v * 100) / 100);
        });
        projected.push([data.projected.high.mean[i][0], '', ...yearRes.flat()]);
      }

      return {
        historical,
        projected
      };
    } catch {
      return {
        historical: [],
        projected: []
      };
    }
  }
);

export const selectClimateMinMax = createSelector(
  selectData,
  selectCurrentLocation,
  selectUseStationData,
  (data, loc, useStation) => {
    try {
      let intermediateProjData, highProjData, veryHighProjData, min, max;
      
      if (useStation) {
        min = 999;
        max = -999;
        data.station.past.forEach(arr => {
          if (arr[1] < min) {
            min = arr[1];
          }
          if (arr[1] > max) {
            max = arr[1];
          }
        });

        intermediateProjData = data.station.projected.intermediate;
        highProjData = data.station.projected.high;        
        veryHighProjData = data.station.projected.veryHigh;        
      } else {
        const stateOrCounty = loc.id.length === 2 ? 'state' : 'county';
        intermediateProjData = data.projected.intermediate;
        highProjData = data.projected.high;
        veryHighProjData = data.projected.veryHigh;

        min = Math.min(...data.raw[stateOrCounty].data[loc.id]);
        max = Math.max(...data.raw[stateOrCounty].data[loc.id]);
      }

      [intermediateProjData, highProjData, veryHighProjData].forEach((dataSet) => {
        for (let i = 0; i < dataSet.range.length; i++) {
          const lower = dataSet.range[i][1];
          if (lower < min) min = lower;

          const upper = dataSet.range[i][2];
          if (upper > max) max = upper;
        }
      });
      return { min, max };
    } catch {
      return { min: 0, max: 0 };
    }
  }
);

export const selectHazardChartData = createSelector(
  selectData,
  selectCurrentLocation,
  selectHazardMetric,
  selectDateRange,
  (data, loc, metric, dateRange) => {
    try {
      let hazardData = [];
      if (loc.id.length === 2) {
        hazardData = data.raw.state[metric][loc.id];
      } else {
        const partialData = data.raw.county[metric][loc.id];

        for (let i = 1996; i < new Date().getFullYear(); i++) {
          const year = partialData.find((arr) => arr[0] === i);
          hazardData.push(year ? year : [i, 0]);
        }
      }

      const startIdx = hazardData.findIndex(
        (arr) => arr[0] === dateRange.start
      );
      const endIdx = hazardData.findIndex((arr) => arr[0] === dateRange.end);
      const slicedData = hazardData.slice(startIdx, endIdx + 1);
      return slicedData.find((arr) => arr[1] > 0) ? slicedData : [];
    } catch {
      return [];
    }
  }
);

export const selectHazardCsvData = createSelector(
  selectData,
  selectCurrentLocation,
  selectHazardMetric,
  selectDateRange,
  (data, loc, metric, dateRange) => {
    try {
      const level = loc.id.length === 2 ? 'state' : 'county';
      return Object.keys(data.raw[level]).reduce((acc, metric, i) => {
        let metricData = [];
        const partialData = data.raw[level][metric][loc.id];
        if (level === 'state') {
          metricData = partialData;
        } else {
          for (let i = 1996; i < new Date().getFullYear(); i++) {
            const year = partialData.find((arr) => arr[0] === i);
            metricData.push(year ? year : [i, 0]);
          }
        }

        const startIdx = metricData.findIndex(
          (arr) => arr[0] === dateRange.start
        );
        const endIdx = metricData.findIndex((arr) => arr[0] === dateRange.end);
        const slicedData = metricData.slice(startIdx, endIdx + 1);
        acc[metric] = slicedData.find((arr) => arr[1] > 0) ? slicedData : [];
        return acc;
      }, {});
    } catch {
      return {};
    }
  }
);

export const selectModelTrend = createSelector(
  selectProjectedChartData,
  ({ pastMean, mean }) => {
    const yearsToAvg = 30;

    try {
      const pastAvg =
        pastMean.reduce((acc, arr) => acc + arr[1], 0) / yearsToAvg;
      const projectedAvg =
        mean.slice(0, yearsToAvg).reduce((acc, arr) => acc + arr[1], 0) /
        yearsToAvg;
      return {
        avgDiff: Math.round((projectedAvg - pastAvg) * 100) / 100,
        pastTimeRange: `${pastMean[0][0]}-${pastMean[yearsToAvg - 1][0]}`,
        projectedTimeRange: `${mean[0][0]}-${mean[yearsToAvg - 1][0]}`,
      };
    } catch {
      return { avgDiff: 0, pastTimeRange: '', projectedTimeRange: '' };
    }
  }
);

export const selectLocationEpisodes = createSelector(
  selectData,
  selectCurrentLocation,
  (data, loc) => {
    try {
      return data.raw[loc.id.length === 2 ? 'state' : 'county'].episodes[
        loc.id
      ];
    } catch {
      return [];
    }
  }
);

export const selectStationData = createSelector(
  selectData,
  (data) => data.station
);